import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RoutesString from "pages/routesString";
import {
  Box,
  Center,
  Group,
  Image,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import iconTransaction from "../images/card-trasaction.png";
import ItemLayout from "../items/ItemLayout";
import ListNotificationsLayout from "./ListNotificationsLayout";
import {
  useGetNotificationStickyQuery,
  useMarkAsReadMutation,
} from "@/services/notificationAPI";
import { NsType } from "@/dto/notifications/response/enum";
import { encryptObject, parseTimeAndDate } from "@/utils/common";
import useRolePermission from "@/hooks/useRolePermission";
import { NotificationStickies } from "@/dto/notifications/response/NotificationResponse.d";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/stores";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const PAGE_SIZE = 20;

/** Convert any date string to "HH:mm:ss DD/MM/YYYY" that parseTimeAndDate expects.
 *  Handles ISO 8601 ("2026-05-13T03:50:18.000+00:00") and already-formatted strings. */
const toStandardDateStr = (raw: string): string => {
  if (!raw) return "";
  if (/^\d{2}:\d{2}:\d{2} \d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  return `${hh}:${mm}:${ss} ${dd}/${mo}/${d.getFullYear()}`;
};

interface NotificationListPersonalProps {
  setActiveDrawer?: any;
  /** Category filter from the drawer's filter buttons. "ALL" = no filter. */
  selectedType?: string;
  /** Date range from the filter drawer (raw Date objects). */
  fromDate?: Date | null;
  toDate?: Date | null;
  hideSeeAll?: boolean;
}

/** Convert a Date to "yyyyMMdd" required by the API. */
const toApiDate = (date: Date | null | undefined): string | undefined => {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
};

interface NotiGroup {
  date: string;
  data: NotificationStickies[];
}

const groupByDate = (items: NotificationStickies[]): NotiGroup[] =>
  Object.values(
    items.reduce((acc: Record<string, NotiGroup>, item) => {
      const { date } = parseTimeAndDate(item.startTime);
      const key = date || "unknown";
      if (!acc[key]) acc[key] = { date: key, data: [] };
      acc[key].data.push(item);
      return acc;
    }, {}),
  ) as NotiGroup[];

const NotificationListPersonal = ({
  setActiveDrawer,
  selectedType = "ALL",
  fromDate,
  toDate,
  hideSeeAll = false,
}: NotificationListPersonalProps) => {
  const navigate = useNavigate();
  const { isChecker } = useRolePermission();
  const { t } = useTranslation();
  const [markAsRead] = useMarkAsReadMutation();

  // ── Pagination state ──────────────────────────────────────────────────────
  const [pageNo, setPageNo] = useState(1);
  const [allApiItems, setAllApiItems] = useState<NotificationStickies[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);

  const { data, isFetching } = useGetNotificationStickyQuery({
    nsType: NsType.Private,
    pageNo,
    pageSize: PAGE_SIZE,
    type: selectedType !== "ALL" ? selectedType : undefined,
    fromDate: toApiDate(fromDate),
    toDate: toApiDate(toDate),
  });

  // Accumulate pages: page 1 resets the list; subsequent pages append.
  // Normalize startTime here so every item — regardless of the format the API
  // returns — is always in "HH:mm:ss DD/MM/YYYY" before grouping/display.
  useEffect(() => {
    if (!data?.body) return;
    const newItems = (data.body.privateNotifications ?? []).map((item) => ({
      ...item,
      startTime: toStandardDateStr(item.startTime),
    }));
    setAllApiItems((prev) => (pageNo === 1 ? newItems : [...prev, ...newItems]));
    setHasMore(pageNo < (data.body.totalPages ?? 1));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  // ^^^ intentionally omit pageNo — we only want to run when new data arrives

  // ── Intersection Observer — load next page when sentinel enters view ───────
  useEffect(() => {
    const el = bottomSentinelRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isFetching) {
          setPageNo((prev) => prev + 1);
        }
      },
      { rootMargin: "120px" }, // trigger slightly before the sentinel is fully visible
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isFetching]);

  // ── Real-time WS notifications from Redux (prepended to top) ──────────────
  const realtimeNotifications = useSelector(
    (state: RootState) => state.notificationBell.realtimeNotifications,
  );

  // ── Build display list ────────────────────────────────────────────────────
  const rtItems: NotificationStickies[] = realtimeNotifications
    .filter((ws) => !allApiItems.some((a) => a.transId === ws.transId))
    .map((ws) => ({
      id: ws.rootId,
      nsType: "U",
      type: ws.templateType,
      templateType: ws.templateType,
      title: ws.title,
      message: ws.message,
      shortContent: ws.description,
      description: ws.description,
      viewStatus: ws.viewStatus,
      startTime: toStandardDateStr(ws.createDatetime),
      transId: ws.transId,
      priority: String(ws.priority),
      urlDetail: ws.urlDetail,
    }));

  const dataNoti = groupByDate([...rtItems, ...allApiItems]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleNotificationClick = useCallback(
    (noti: NotificationStickies) => {
      if (noti.viewStatus !== "Y" && noti.id) {
        markAsRead({ id: noti.id, nsType: noti.nsType || "U" });
      }
      setActiveDrawer?.(null);
      if (noti.urlDetail) {
        navigate(noti.urlDetail);
        return;
      }
      const businessType = noti.templateType || noti.type;
      // Đề nghị giải ngân có màn duyệt RIÊNG (checker flow) → điều hướng thẳng theo requestId,
      // không dùng màn duyệt giao dịch tổng quát (chưa handle businessType này).
      const bt = (businessType || "").toUpperCase();
      if ((bt.includes("DISBURSEMENT") || bt.includes("DSB")) && noti.transId) {
        // Checker → màn duyệt riêng (?requestId=); Maker → màn chi tiết đề nghị (xem + hủy).
        if (isChecker) {
          navigate(`${RoutesString.Disbursement}?requestId=${encodeURIComponent(noti.transId)}`);
        } else {
          navigate(RoutesString.DisbursementDetail.replace(":code", encodeURIComponent(noti.transId)));
        }
        return;
      }
      if (noti.transId && businessType) {
        const encryptCode = encryptObject({ transId: noti.transId, businessType });
        navigate(
          `${RoutesString.TransactionDetail}/${encodeURIComponent(encryptCode)}`,
        );
      }
    },
    [markAsRead, navigate, setActiveDrawer, isChecker],
  );

  const getNameDay = (date: string) => {
    if (!date) return "";
    const day = dayjs(date, "DD/MM/YYYY");
    if (day.isSame(dayjs(), "day")) return t("TODAY");
    if (day.isSame(dayjs().subtract(1, "day"), "day")) return t("YESTERDAY");
    return date;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Stack spacing={0}>
      <Group position="apart" mt={16}>
        <Text weight={600} size={14} color="#111827" />
        {!hideSeeAll && (
          <Text
            size={14}
            color="#2A81D0"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              navigate(RoutesString.Notifications);
              setActiveDrawer?.(null);
            }}
          >
            {t("SEE_ALL")}
          </Text>
        )}
      </Group>

      {dataNoti.map((group) => (
        <Box key={group.date}>
          <Group position="apart" mb={16}>
            <Text weight={600} size={14} color="#111827" mt={16}>
              {getNameDay(group.date)}
            </Text>
          </Group>

          {group.data.map((noti, index) => {
            const { time } = parseTimeAndDate(noti.startTime);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={`${noti.id}-${index}`}>
                <ListNotificationsLayout
                  content={
                    <ItemLayout
                      onClick={() => handleNotificationClick(noti)}
                      style={{
                        backgroundColor:
                          noti.viewStatus === "Y" ? "#FDFDFD" : "#E5F4FE",
                        cursor: "pointer",
                      }}
                      title={
                        <Group spacing={12} mb={10}>
                          <Box sx={{ width: "24px", height: "24px" }}>
                            <Image src={iconTransaction} fit="contain" />
                          </Box>
                          <Text size={14} weight={600} color="#111827">
                            {noti.title}
                          </Text>
                        </Group>
                      }
                      content={noti.shortContent || noti.message}
                      bottom={
                        <Group position="apart" mt={10}>
                          <Text size={12} color="#9CA3AF">
                            {time}
                          </Text>
                        </Group>
                      }
                    />
                  }
                />
              </React.Fragment>
            );
          })}
        </Box>
      ))}

      {/* Bottom sentinel — IntersectionObserver watches this element */}
      <div ref={bottomSentinelRef} style={{ height: 1 }} />

      {isFetching && (
        <Center py={12}>
          <Loader size="sm" color="blue" />
        </Center>
      )}

      {!hasMore && !isFetching && allApiItems.length > 0 && (
        <Text size={12} color="#9CA3AF" align="center" py={12}>
          Đã tải hết thông báo
        </Text>
      )}
    </Stack>
  );
};

export default NotificationListPersonal;
